import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { AppInfoIcon } from '@/UI/AppInfoIcon/AppInfoIcon';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { TextSmMd, TextSmRg, Typography } from '@/UI/AppTypography/AppTypography';
import { InfoIcon } from '@/UI/Icons';
import { Flex, FlexCenVer, FlexCol, FlexCenVerSpaceBetween } from '@/UI/styles';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import type { IteratorRunMode } from '@/types/node';
import type { JSX } from 'react';

const iconStroke = color.White64_T;

function ParallelIterationIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      {[8, 16, 24].map((y) => (
        <g key={y}>
          <circle cx="8" cy={y} r="2.5" stroke={iconStroke} />
          <circle cx="24" cy={y} r="2.5" stroke={iconStroke} />
          <path d={`M10.5 ${y}H21.5`} stroke={iconStroke} strokeLinecap="round" />
        </g>
      ))}
    </svg>
  );
}

function MatrixIterationIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <circle cx="8" cy="16" r="2.5" stroke={iconStroke} />
      {[8, 16, 24].map((y) => (
        <circle key={y} cx="24" cy={y} r="2.5" stroke={iconStroke} />
      ))}
      <path d="M10.5 16L21.5 8" stroke={iconStroke} strokeLinecap="round" />
      <path d="M10.5 16H21.5" stroke={iconStroke} strokeLinecap="round" />
      <path d="M10.5 16L21.5 24" stroke={iconStroke} strokeLinecap="round" />
    </svg>
  );
}

type IterationModePickerProps = {
  disabled?: boolean;
  mode: IteratorRunMode;
  onChange: (mode: IteratorRunMode) => void;
  variant?: 'panel' | 'node';
};

const pickerOptions: Array<{
  mode: IteratorRunMode;
  shortLabelKey: string;
  titleKey: string;
  descriptionKey: string;
  footnoteKey?: string;
  Icon: () => JSX.Element;
}> = [
  {
    mode: 'parallel',
    shortLabelKey: I18N_KEYS.RECIPE_MAIN.FLOW.ITERATORS_SECTION.ITERATION_MODE_PARALLEL_SHORT,
    titleKey: I18N_KEYS.RECIPE_MAIN.FLOW.ITERATORS_SECTION.ITERATION_MODE_PARALLEL_TITLE,
    descriptionKey: I18N_KEYS.RECIPE_MAIN.FLOW.ITERATORS_SECTION.ITERATION_MODE_PARALLEL_DESCRIPTION,
    footnoteKey: I18N_KEYS.RECIPE_MAIN.FLOW.ITERATORS_SECTION.ITERATION_MODE_PARALLEL_FOOTNOTE,
    Icon: ParallelIterationIcon,
  },
  {
    mode: 'matrix',
    shortLabelKey: I18N_KEYS.RECIPE_MAIN.FLOW.ITERATORS_SECTION.ITERATION_MODE_MATRIX_SHORT,
    titleKey: I18N_KEYS.RECIPE_MAIN.FLOW.ITERATORS_SECTION.ITERATION_MODE_MATRIX_TITLE,
    descriptionKey: I18N_KEYS.RECIPE_MAIN.FLOW.ITERATORS_SECTION.ITERATION_MODE_MATRIX_DESCRIPTION,
    Icon: MatrixIterationIcon,
  },
];

function IterationModeTooltipContent({ useFplSurface = false }: { useFplSurface?: boolean }) {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        ...(!useFplSurface && {
          backgroundColor: token.bgSelected,
          border: `1px solid ${color.White04_T}`,
          borderRadius: '8px',
        }),
        p: 1.5,
        width: '280px',
      }}
    >
      <FlexCol sx={{ gap: 1.5 }}>
        <Typography variant="label-xs-rg" sx={{ color: token.text }}>
          {t(I18N_KEYS.RECIPE_MAIN.FLOW.ITERATORS_SECTION.ITERATION_MODE_TITLE)}
        </Typography>
        <Flex sx={{ gap: 2 }}>
          {pickerOptions.map(({ mode, titleKey, descriptionKey, footnoteKey, Icon }) => (
            <FlexCol key={mode} sx={{ flex: '1 1 0', gap: 1.5 }}>
              <Icon />
              <FlexCol sx={{ gap: 1 }}>
                <FlexCol sx={{ gap: 0.25 }}>
                  <TextSmMd>{t(titleKey)}</TextSmMd>
                  <TextSmRg sx={{ color: token.text }}>{t(descriptionKey)}</TextSmRg>
                </FlexCol>
                {footnoteKey ? (
                  <Typography variant="body-xs-rg" sx={{ color: token.textSecondary }}>
                    {t(footnoteKey)}
                  </Typography>
                ) : null}
              </FlexCol>
            </FlexCol>
          ))}
        </Flex>
      </FlexCol>
    </Box>
  );
}

export function IterationModePicker({ disabled = false, mode, onChange, variant = 'panel' }: IterationModePickerProps) {
  const { t } = useTranslation();
  const isNodeVariant = variant === 'node';
  const isFplMigrationEnabled = useFplMigrationFlag();
  const selectedBackgroundColor = isNodeVariant ? color.White08_T : color.White16_T;
  const labelVariant = isNodeVariant ? 'body-sm-rg' : 'label-xs-rg';
  const labelColor = isNodeVariant ? color.White100 : color.White64_T;
  const buttonPaddingX = isNodeVariant ? 1 : 1.75;

  const label = (
    <FlexCenVer sx={{ gap: 0.5, minWidth: 0 }}>
      <Typography variant={labelVariant} sx={{ color: labelColor }}>
        {t(I18N_KEYS.RECIPE_MAIN.FLOW.ITERATORS_SECTION.ITERATION_MODE_TITLE)}
      </Typography>
      {isFplMigrationEnabled ? (
        <Flex sx={{ color: token.textSecondary }}>
          <AppInfoIcon
            tooltipProps={{
              title: <IterationModeTooltipContent useFplSurface />,
              placement: isNodeVariant ? 'top' : 'top-start',
            }}
          />
        </Flex>
      ) : (
        <Tooltip
          title={<IterationModeTooltipContent />}
          placement={isNodeVariant ? 'top' : 'top-start'}
          enterDelay={600}
          enterNextDelay={600}
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: 'transparent',
                p: 0,
                maxWidth: 'none',
              },
            },
          }}
        >
          <Box
            component="span"
            sx={{
              color: token.textSecondary,
              display: 'flex',
              '&:hover': { color: token.text },
            }}
          >
            <InfoIcon width={16} height={16} />
          </Box>
        </Tooltip>
      )}
    </FlexCenVer>
  );

  const toggle = (
    <Flex
      sx={{
        width: isNodeVariant ? '118px' : '100%',
        minWidth: isNodeVariant ? '118px' : 0,
        height: '24px',
        backgroundColor: token.bgTransparent,
        border: `1px solid ${color.White04_T}`,
        borderRadius: '4px',
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      {pickerOptions.map(({ mode: optionMode, shortLabelKey }, index) => {
        const isSelected = mode === optionMode;
        return (
          <Box
            key={optionMode}
            component="button"
            type="button"
            onClick={() => onChange(optionMode)}
            disabled={disabled}
            sx={{
              alignItems: 'center',
              backgroundColor: isSelected ? selectedBackgroundColor : 'transparent',
              border: 'none',
              color: isSelected ? token.text : token.textSecondary,
              cursor: disabled ? 'default' : 'pointer',
              display: 'flex',
              flex: '1 1 0',
              justifyContent: 'center',
              opacity: disabled ? 0.72 : 1,
              px: buttonPaddingX,
              '&:not(:last-of-type)': {
                borderRight: index === 0 ? `1px solid ${color.White04_T}` : undefined,
              },
            }}
          >
            <TextSmRg sx={{ color: 'inherit' }}>{t(shortLabelKey)}</TextSmRg>
          </Box>
        );
      })}
    </Flex>
  );

  if (isNodeVariant) {
    return (
      <FlexCenVerSpaceBetween sx={{ width: '100%', gap: 1.5 }}>
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>{label}</Box>
        {toggle}
      </FlexCenVerSpaceBetween>
    );
  }

  return (
    <FlexCol sx={{ width: '100%', gap: 0.75 }}>
      {label}
      {toggle}
    </FlexCol>
  );
}
